import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';

import { ImageCropLayout, imageCropImgStyle } from '../../../_components/rich-editor/extensions/image-crop.util';

export interface LightboxImage {
  src: string;
  alt: string;
  naturalWidth: number;
  naturalHeight: number;
  /** Set for a cropped image: only its visible part is shown. */
  crop?: ImageCropLayout | null;
}

const MAX_SCALE = 8;
const TAP_SLOP_PX = 8;
const TAP_MAX_MS = 500;
const VIEWPORT_MARGIN_PX = 16;

/**
 * An image shown as large as fits the window over the dimmed page (View → click/tap an image).
 * Pinch (finger or trackpad), mouse wheel and the +/− buttons zoom in and out around the pointer;
 * a zoomed image is moved by dragging. A click/tap that isn't a drag or pinch closes it, as does
 * the × button or Escape (handled by the host).
 */
@Component({
  selector: 'app-image-lightbox',
  standalone: true,
  template: `
    <div
      #overlay
      class="image-zoom-overlay print:hidden"
      role="dialog"
      aria-modal="true"
      [attr.aria-label]="image.alt || 'Image'"
      data-testid="image-lightbox">
      <div #box class="image-zoom-overlay__box" [class.is-cropped]="!!image.crop">
        <img
          class="image-zoom-overlay__img"
          [src]="image.src"
          [alt]="image.alt"
          [attr.style]="imgStyle"
          draggable="false" />
      </div>

      <div class="image-zoom-overlay__controls" (pointerdown)="$event.stopPropagation()" (click)="$event.stopPropagation()">
        <button #zoomOutBtn type="button" class="image-zoom-overlay__btn" aria-label="Zoom out" title="Zoom out" disabled (click)="zoomBy(1 / 1.5)">
          <span class="pi pi-minus"></span>
        </button>
        <button #zoomLevelBtn type="button" class="image-zoom-overlay__btn image-zoom-overlay__btn--pct" title="Fit to window" data-testid="lightbox-zoom-level" (click)="resetZoom(true)"></button>
        <button #zoomInBtn type="button" class="image-zoom-overlay__btn" aria-label="Zoom in" title="Zoom in" (click)="zoomBy(1.5)">
          <span class="pi pi-plus"></span>
        </button>
        <span class="image-zoom-overlay__sep"></span>
        <button type="button" class="image-zoom-overlay__btn" aria-label="Close" title="Close (Esc)" (click)="closed.emit()">
          <span class="pi pi-times"></span>
        </button>
      </div>
    </div>
  `,
})
export class ImageLightboxComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) image!: LightboxImage;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('overlay', { static: true }) overlayRef!: ElementRef<HTMLElement>;
  @ViewChild('box', { static: true }) boxRef!: ElementRef<HTMLElement>;
  @ViewChild('zoomOutBtn', { static: true }) zoomOutBtn!: ElementRef<HTMLButtonElement>;
  @ViewChild('zoomInBtn', { static: true }) zoomInBtn!: ElementRef<HTMLButtonElement>;
  @ViewChild('zoomLevelBtn', { static: true }) zoomLevelBtn!: ElementRef<HTMLButtonElement>;

  /** Zoom relative to "fits the window" (1). */
  scale = 1;
  private tx = 0;
  private ty = 0;
  private fitWidth = 0;
  private fitHeight = 0;
  /** Size of the fitted image relative to its natural size — the zoom level shown is scale × this. */
  private fitFactor = 1;

  private readonly zone = inject(NgZone);
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private gesture: {
    startScale: number;
    startTx: number;
    startTy: number;
    startDist: number;
    startMid: { x: number; y: number };
    startTime: number;
    moved: boolean;
    multi: boolean;
  } | null = null;
  private readonly cleanups: Array<() => void> = [];
  /** The last press was a tap (no drag, no pinch): its click closes the lightbox. */
  private tapped = false;

  get imgStyle(): string | null {
    return this.image.crop ? imageCropImgStyle(this.image.crop) : null;
  }

  get zoomPercent(): number {
    return Math.round(this.scale * this.fitFactor * 100);
  }

  ngAfterViewInit(): void {
    this.layout();
    const overlay = this.overlayRef.nativeElement;
    // Outside Angular: pointer moves don't need change detection (the transform is set directly).
    this.zone.runOutsideAngular(() => {
      const listen = <K extends keyof HTMLElementEventMap>(type: K, fn: (e: HTMLElementEventMap[K]) => void, opts?: AddEventListenerOptions) => {
        overlay.addEventListener(type, fn as EventListener, opts);
        this.cleanups.push(() => overlay.removeEventListener(type, fn as EventListener, opts));
      };
      listen('pointerdown', (e) => this.onPointerDown(e));
      listen('pointermove', (e) => this.onPointerMove(e));
      listen('pointerup', (e) => this.onPointerUp(e, true));
      listen('pointercancel', (e) => this.onPointerUp(e, false));
      listen('wheel', (e) => this.onWheel(e), { passive: false });
      // Closed on the click that follows a tap (not on pointerup), so that click can't fall through
      // to the note underneath and open the image again.
      listen('click', () => {
        if (!this.tapped) return;
        this.tapped = false;
        this.zone.run(() => this.closed.emit());
      });
      // iOS Safari: keep a pinch on the image from zooming the whole page.
      listen('gesturestart' as keyof HTMLElementEventMap, (e) => e.preventDefault(), { passive: false });
      listen('touchmove', (e) => e.preventDefault(), { passive: false });
    });
  }

  ngOnDestroy(): void {
    this.cleanups.forEach((fn) => fn());
  }

  @HostListener('window:resize')
  onResize(): void {
    this.layout();
  }

  @HostListener('document:keydown', ['$event'])
  onKey(event: KeyboardEvent): void {
    if (event.key === '+' || event.key === '=') this.zoomBy(1.5);
    else if (event.key === '-') this.zoomBy(1 / 1.5);
    else if (event.key === '0') this.resetZoom(true);
    else return;
    event.preventDefault();
  }

  zoomBy(factor: number, center?: { x: number; y: number }): void {
    this.zoomTo(this.scale * factor, center, true);
  }

  resetZoom(animate: boolean): void {
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.apply(animate);
  }

  /** Sizes the image to fit the window (small images are enlarged too), keeping its proportions. */
  private layout(): void {
    const { naturalWidth, naturalHeight, crop } = this.image;
    const width = Math.max(1, (naturalWidth || 1) * (crop?.visibleWidth ?? 1));
    const height = Math.max(1, (naturalHeight || 1) * (crop?.visibleHeight ?? 1));
    const availWidth = Math.max(50, window.innerWidth - 2 * VIEWPORT_MARGIN_PX);
    const availHeight = Math.max(50, window.innerHeight - 2 * VIEWPORT_MARGIN_PX);
    this.fitFactor = Math.min(availWidth / width, availHeight / height);
    this.fitWidth = width * this.fitFactor;
    this.fitHeight = height * this.fitFactor;
    const box = this.boxRef.nativeElement;
    box.style.width = `${this.fitWidth}px`;
    box.style.height = `${this.fitHeight}px`;
    this.apply(false);
  }

  /** Zooms keeping the image point under `center` (viewport coordinates) where it is. */
  private zoomTo(nextScale: number, center: { x: number; y: number } | undefined, animate: boolean): void {
    const clamped = Math.min(MAX_SCALE, Math.max(1, nextScale));
    const c = center ?? { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const rel = { x: c.x - window.innerWidth / 2, y: c.y - window.innerHeight / 2 };
    const ratio = clamped / this.scale;
    this.tx = rel.x - (rel.x - this.tx) * ratio;
    this.ty = rel.y - (rel.y - this.ty) * ratio;
    this.scale = clamped;
    this.apply(animate);
  }

  /** Keeps a zoomed image covering the window (no empty band on a side it overflows). */
  private clampPan(): void {
    const maxX = Math.max(0, (this.fitWidth * this.scale - window.innerWidth) / 2 + VIEWPORT_MARGIN_PX);
    const maxY = Math.max(0, (this.fitHeight * this.scale - window.innerHeight) / 2 + VIEWPORT_MARGIN_PX);
    this.tx = Math.min(maxX, Math.max(-maxX, this.tx));
    this.ty = Math.min(maxY, Math.max(-maxY, this.ty));
    if (this.scale <= 1.001) {
      this.tx = 0;
      this.ty = 0;
    }
  }

  private apply(animate: boolean): void {
    this.clampPan();
    const box = this.boxRef.nativeElement;
    box.style.transition = animate ? 'transform 160ms ease-out' : 'none';
    box.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
    this.overlayRef.nativeElement.classList.toggle('is-zoomed', this.scale > 1.001);
    // Set directly: these change on every pointer move, which needs no change detection.
    this.zoomLevelBtn.nativeElement.textContent = `${this.zoomPercent}%`;
    this.zoomOutBtn.nativeElement.disabled = this.scale <= 1.001;
    this.zoomInBtn.nativeElement.disabled = this.scale >= MAX_SCALE - 0.001;
  }

  private onPointerDown(e: PointerEvent): void {
    if ((e.target as HTMLElement).closest('.image-zoom-overlay__controls')) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    this.tapped = false;
    try {
      this.overlayRef.nativeElement.setPointerCapture?.(e.pointerId);
    } catch {
      // The pointer is already gone (or synthetic) — moves still reach the overlay.
    }
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.startGesture(this.gesture?.moved ?? false, this.gesture?.multi ?? false);
    if (this.pointers.size > 1 && this.gesture) this.gesture.multi = true;
  }

  private startGesture(moved: boolean, multi: boolean): void {
    const points = [...this.pointers.values()];
    if (!points.length) {
      this.gesture = null;
      return;
    }
    this.gesture = {
      startScale: this.scale,
      startTx: this.tx,
      startTy: this.ty,
      startDist: points.length > 1 ? Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) : 0,
      startMid: points.length > 1 ? { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 } : { ...points[0] },
      startTime: this.gesture?.startTime ?? performance.now(),
      moved,
      multi,
    };
  }

  private onPointerMove(e: PointerEvent): void {
    if (!this.pointers.has(e.pointerId) || !this.gesture) return;
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = this.gesture;
    const points = [...this.pointers.values()];

    if (points.length > 1) {
      const dist = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
      const mid = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
      g.moved = true;
      // Zoom around where the pinch started, then follow the fingers' midpoint.
      this.scale = g.startScale;
      this.tx = g.startTx;
      this.ty = g.startTy;
      const next = Math.min(MAX_SCALE, Math.max(1, g.startScale * (g.startDist ? dist / g.startDist : 1)));
      const rel = { x: g.startMid.x - window.innerWidth / 2, y: g.startMid.y - window.innerHeight / 2 };
      const ratio = next / this.scale;
      this.tx = rel.x - (rel.x - this.tx) * ratio + (mid.x - g.startMid.x);
      this.ty = rel.y - (rel.y - this.ty) * ratio + (mid.y - g.startMid.y);
      this.scale = next;
      this.apply(false);
      return;
    }

    const dx = points[0].x - g.startMid.x;
    const dy = points[0].y - g.startMid.y;
    if (!g.moved && Math.hypot(dx, dy) > TAP_SLOP_PX) g.moved = true;
    if (g.moved && this.scale > 1.001) {
      this.tx = g.startTx + dx;
      this.ty = g.startTy + dy;
      this.apply(false);
    }
  }

  private onPointerUp(e: PointerEvent, completed: boolean): void {
    if (!this.pointers.has(e.pointerId)) return;
    this.pointers.delete(e.pointerId);
    const g = this.gesture;
    if (this.pointers.size > 0) {
      // A finger lifted during a pinch: carry on panning with the other one.
      this.startGesture(true, true);
      return;
    }
    this.gesture = null;
    this.tapped = completed && !!g && !g.moved && !g.multi && performance.now() - g.startTime < TAP_MAX_MS;
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    // Trackpad pinch arrives as a wheel event with ctrlKey and small deltas.
    const intensity = e.ctrlKey ? 0.01 : 0.002;
    const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    this.zoomTo(this.scale * Math.exp(-delta * intensity), { x: e.clientX, y: e.clientY }, false);
  }
}
